import { FC } from 'react';

import { Badge, ColorDot } from '@purpur/library';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { getSvgIcon } from '@telialt/common-icon/src';
import { SPECIAL_DISPLAY_COLORS } from '@telialt/utils/src/eshopConstants';
import { withParams } from '@telialt/utils/src/helpers';

import { ScreenReaderOnly } from '../../SearchComponents/ScreenReaderOnly/ScreenReaderOnly.styles';
import { CompareRow } from './CompareRow';
import { ProductCardProps } from './model';
import {
    BadgeWrapper,
    BenefitIcon,
    BenefitItem,
    BenefitLabel,
    BenefitsBlock,
    CardWrapper,
    ClickableArea,
    ColorDotsWrapper,
    CurrencyTextBold,
    FullPriceBlock,
    GrayText,
    ImageWrapper,
    InfoWrapper,
    LeasingInitialAmountBlock,
    MarketingTag,
    MonthlyPriceLabelText,
    MultiColorDot,
    OldPrice,
    PlanCommitmentBlock,
    PriceBlock,
    PriceBlockContent,
    PriceText,
    StockStatus,
    StyledHeading,
    StyledImage,
    TransparentColorDot,
} from './ProductCard.styles';

export const ProductCard: FC<ProductCardProps> = ({
    name,
    image,
    imageAlt,
    monthlyPrice,
    commitment,
    planCommitment,
    fullPrice,
    oldFullPrice,
    residualValue,
    badges,
    marketingTag,
    marketingTagAltText,
    url,
    colorVariants,
    stockStatus,
    benefits,
    leasingInitialAmount,
    onClick,
    isCompareChecked,
    isCompareDisabled,
    compareDisabledMessage,
    onCompare,
    headingTag = 'h2',
}) => {
    const { t } = useTranslations();

    const stockStatusLabel =
        stockStatus === 'Turime prekyboje' ? undefined : t['search.component.product_card.out_of_stock'];

    const labelMonthlyPrice =
        !!monthlyPrice &&
        [
            t['search.component.product_card.monthly_price_label'],
            monthlyPrice,
            t['search.component.product_card.monthly_price_unit_accessible'],
        ].join(' ');
    const labelFullPrice = !!fullPrice && [fullPrice, t['search.component.product_card.full_price_unit']].join(' ');

    const labelPrice = labelMonthlyPrice || labelFullPrice || '';

    const ariaLabel = [name, labelPrice, stockStatusLabel].filter(Boolean).join(', ');

    return (
        <CardWrapper>
            <ClickableArea href={url} onClick={onClick} draggable={false} aria-label={ariaLabel} data-element="Product">
                <BadgeWrapper>
                    {badges?.map((badge, index) => (
                        <Badge key={index} showIcon={false} variant="information">
                            {badge}
                        </Badge>
                    ))}
                </BadgeWrapper>
                <ImageWrapper>
                    <StyledImage src={image} alt={imageAlt || name} />
                    {marketingTag && (
                        // TODO: marketing tag will be updated to dynamic with feature LTCONART-815, for now making this image decorative only
                        <MarketingTag src={marketingTag} alt={marketingTagAltText || ''} />
                    )}
                </ImageWrapper>
                <StockStatus>{stockStatusLabel}</StockStatus>
                <StyledHeading tag={headingTag} variant="title-100">
                    {name}
                </StyledHeading>
                <ColorDotsWrapper>
                    {colorVariants?.length > 0 &&
                        colorVariants.map((color) => {
                            if (color.value === SPECIAL_DISPLAY_COLORS.MULTICOLOR) {
                                return <MultiColorDot key={color.code} />;
                            } else if (color.value === SPECIAL_DISPLAY_COLORS.TRANSPARENT) {
                                return <TransparentColorDot key={color.code} />;
                            } else {
                                return (
                                    <ColorDot
                                        key={color.code}
                                        color={color.value}
                                        size="xs"
                                        withBorder={SPECIAL_DISPLAY_COLORS.WHITE_ARRAY.includes(color.value)}
                                    />
                                );
                            }
                        })}
                </ColorDotsWrapper>

                {(monthlyPrice || fullPrice) && (
                    <InfoWrapper>
                        <PriceBlock>
                            {(monthlyPrice || leasingInitialAmount) && (
                                <PriceBlockContent>
                                    {monthlyPrice && (
                                        <div>
                                            <div>
                                                <MonthlyPriceLabelText>
                                                    {t['search.component.product_card.monthly_price_label']}
                                                </MonthlyPriceLabelText>
                                                <PriceText>{monthlyPrice}</PriceText>
                                                <CurrencyTextBold>
                                                    <span aria-hidden="true">
                                                        {t['search.component.product_card.monthly_price_unit']}
                                                    </span>
                                                    <ScreenReaderOnly>
                                                        {
                                                            t[
                                                                'search.component.product_card.monthly_price_unit_accessible'
                                                            ]
                                                        }
                                                    </ScreenReaderOnly>
                                                </CurrencyTextBold>
                                            </div>
                                            {commitment && (
                                                <div>
                                                    <GrayText>{commitment}</GrayText>
                                                </div>
                                            )}
                                            {!!residualValue && (
                                                <GrayText>
                                                    {withParams(t['search.component.product_card.residual_value'], {
                                                        price: residualValue,
                                                    })}
                                                </GrayText>
                                            )}
                                        </div>
                                    )}
                                    {leasingInitialAmount && (
                                        <LeasingInitialAmountBlock>
                                            <GrayText>
                                                {withParams(t['search.component.product_card.leasing_initial_amount'], {
                                                    price: leasingInitialAmount,
                                                })}
                                            </GrayText>
                                        </LeasingInitialAmountBlock>
                                    )}
                                </PriceBlockContent>
                            )}
                            {fullPrice && (
                                <FullPriceBlock $hasMonthlyPrice={!!monthlyPrice}>
                                    <div>
                                        <PriceText>{fullPrice}</PriceText>
                                        <CurrencyTextBold>
                                            {t['search.component.product_card.full_price_unit']}
                                        </CurrencyTextBold>
                                    </div>
                                    {oldFullPrice && (
                                        <OldPrice aria-hidden="true">
                                            {withParams(t['search.component.product_card.old_full_price'], {
                                                price: oldFullPrice,
                                            })}
                                        </OldPrice>
                                    )}
                                </FullPriceBlock>
                            )}
                        </PriceBlock>
                        {planCommitment && (
                            <PlanCommitmentBlock>
                                {withParams(t['search.component.product_card.plan_commitment'], { planCommitment })}
                            </PlanCommitmentBlock>
                        )}
                    </InfoWrapper>
                )}
                {!!benefits?.length && (
                    <BenefitsBlock>
                        {benefits.slice(0, 3).map((benefit, index) => (
                            <BenefitItem key={index}>
                                <BenefitIcon svg={getSvgIcon(benefit.icon)} size="md" />
                                <BenefitLabel>{benefit.label}</BenefitLabel>
                            </BenefitItem>
                        ))}
                    </BenefitsBlock>
                )}
            </ClickableArea>
            <CompareRow
                productName={name}
                onCompare={onCompare}
                isCompareChecked={isCompareChecked}
                isCompareDisabled={isCompareDisabled}
                compareDisabledMessage={compareDisabledMessage}
            />
        </CardWrapper>
    );
};
